import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { Box, BoxProps, Typography, alpha, useTheme, styled, Link, Theme } from '@mui/material';
import { motion } from 'framer-motion';
import {
  BarChart2,
  Users,
  Briefcase,
  ExternalLink,
  Home,
  BookOpen,
  HelpCircle,
  Shield,
  Activity,
  Grid,
} from 'lucide-react';
import { PageContainer, MinimalCard } from 'components/styled';
import StandardPageHeader from 'components/StandardPageHeader';
import { selectAuthUser } from 'AuthModule/selectors';
import { getReportTeamsPageDeepLinkUrl } from 'utils';
import { REPORTS_TABLE_TEAMS_V2_PATH, REPORTS_TABLE_MEMBERS_PATH } from '@constants';

/**
 * Shared link card styles for both internal and external links.
 * Uses a lighter background than MinimalCard to create visual hierarchy
 * when nested inside section containers.
 */
const linkCardStyles = (theme: Theme) => ({
  backgroundColor:
    theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.5)' : 'rgba(248, 250, 252, 0.8)',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(148, 163, 184, 0.08)'
      : '1px solid rgba(226, 232, 240, 0.6)',
  borderRadius: '12px',
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  textDecoration: 'none',
  color: 'inherit',
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.8)' : 'rgba(241, 245, 249, 0.9)',
    borderColor:
      theme.palette.mode === 'dark' ? 'rgba(148, 163, 184, 0.15)' : 'rgba(203, 213, 225, 0.8)',
  },
});

const InternalLinkCard = styled(Box)<BoxProps & { component?: React.ElementType; to?: string }>(
  ({ theme }) => linkCardStyles(theme),
);

const ExternalLinkCard = styled(Link)(({ theme }) => linkCardStyles(theme));

const IconContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: theme.spacing(1),
  background: theme.palette.mode === 'dark' ? 'rgba(251, 140, 0, 0.15)' : 'rgba(251, 140, 0, 0.1)',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(251, 140, 0, 0.25)'
      : '1px solid rgba(251, 140, 0, 0.2)',
  color: theme.palette.primary.main,
  flexShrink: 0,
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1rem',
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

interface QuickLink {
  title: string;
  description: string;
  path: string;
  icon: React.ReactNode;
}

interface ExternalLinkItem {
  title: string;
  description: string;
  url: string;
  icon: React.ReactNode;
}

const HomePage = () => {
  const theme = useTheme();
  const authUser = useSelector(selectAuthUser);

  const userName = useMemo(() => {
    if (!authUser?.account) return 'there';
    const { first_name, name } = authUser.account;
    if (first_name) return first_name;
    if (name) return name.split(' ')[0];
    return 'there';
  }, [authUser]);

  const quickLinks: QuickLink[] = [
    {
      title: 'Teams Management',
      description: 'Manage and organize teams',
      path: REPORTS_TABLE_TEAMS_V2_PATH,
      icon: <Briefcase size={20} />,
    },
    {
      title: 'Members',
      description: 'View and manage team members',
      path: REPORTS_TABLE_MEMBERS_PATH,
      icon: <Users size={20} />,
    },
    {
      title: 'Reports',
      description: 'View team reports and analytics',
      path: getReportTeamsPageDeepLinkUrl({}),
      icon: <BarChart2 size={20} />,
    },
  ];

  const externalLinks: ExternalLinkItem[] = [
    {
      title: 'Integrations Readme',
      description: 'Integration setup documentation',
      url: 'https://readme.mosaicapp.com/docs/integration-steps',
      icon: <BookOpen size={20} />,
    },
    {
      title: 'API Reference',
      description: 'Authentication and API docs',
      url: 'https://readme.mosaicapp.com/reference/authentication',
      icon: <BookOpen size={20} />,
    },
    {
      title: 'Help Center',
      description: 'Find answers to common questions',
      url: 'https://support.mosaicapp.com/en',
      icon: <HelpCircle size={20} />,
    },
    {
      title: 'Grafana',
      description: 'Monitoring and dashboards',
      url: 'https://grafana.prod.us-east4.mosaicapp.com/',
      icon: <BarChart2 size={20} />,
    },
    {
      title: 'Trust Page',
      description: 'Security and compliance info',
      url: 'https://trust.mosaicapp.com/',
      icon: <Shield size={20} />,
    },
    {
      title: 'Statuspage',
      description: 'Service status and incidents',
      url: 'https://status.mosaicapp.com/',
      icon: <Activity size={20} />,
    },
    {
      title: 'App Directory',
      description: 'Nudge Security app directory',
      url: 'https://www.nudgesecurity.io/app/directory',
      icon: <Grid size={20} />,
    },
  ];

  return (
    <PageContainer>
      <Helmet title="Home" />

      <StandardPageHeader
        title="Home"
        subtitle="Your dashboard and quick access to key features"
        icon="Home"
        customBreadcrumbs={[{ label: 'Home' }]}
        sx={{ mb: 6 }}
      />

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <MinimalCard $hoverEffect={false} sx={{ mb: 4, p: 4 }}>
          <Box display="flex" alignItems="center" gap={2}>
            <IconContainer sx={{ width: 56, height: 56 }} aria-hidden="true">
              <Home size={28} />
            </IconContainer>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  fontSize: '1.75rem',
                  lineHeight: 1.2,
                }}
              >
                Welcome back, {userName}!
              </Typography>
              <Typography
                sx={{
                  color: alpha(theme.palette.text.secondary, 0.7),
                  fontSize: '0.95rem',
                  mt: 0.5,
                }}
              >
                Here&apos;s your quick access to the Mosaic Galaxy platform
              </Typography>
            </Box>
          </Box>
        </MinimalCard>
      </motion.div>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
          gap: 4,
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <MinimalCard $hoverEffect={false}>
            <SectionTitle>Quick Access</SectionTitle>
            <Box display="flex" flexDirection="column" gap={1.5}>
              {quickLinks.map((link, index) => (
                <motion.div
                  key={link.title}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2, delay: 0.15 + index * 0.05 }}
                >
                  <InternalLinkCard
                    component={RouterLink}
                    to={link.path}
                    aria-label={`${link.title}: ${link.description}`}
                  >
                    <IconContainer aria-hidden="true">{link.icon}</IconContainer>
                    <Box flex={1}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.95rem',
                          color: theme.palette.text.primary,
                        }}
                      >
                        {link.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          color: alpha(theme.palette.text.secondary, 0.7),
                        }}
                      >
                        {link.description}
                      </Typography>
                    </Box>
                  </InternalLinkCard>
                </motion.div>
              ))}
            </Box>
          </MinimalCard>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <MinimalCard $hoverEffect={false}>
            <SectionTitle>
              <ExternalLink size={18} aria-hidden="true" />
              External Resources
            </SectionTitle>
            <Box display="flex" flexDirection="column" gap={1.5}>
              {externalLinks.map((link, index) => (
                <motion.div
                  key={link.title}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.2, delay: 0.25 + index * 0.05 }}
                >
                  <ExternalLinkCard
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${link.title}: ${link.description} (opens in new tab)`}
                  >
                    <IconContainer aria-hidden="true">{link.icon}</IconContainer>
                    <Box flex={1}>
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.95rem',
                          color: theme.palette.text.primary,
                        }}
                      >
                        {link.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.8rem',
                          color: alpha(theme.palette.text.secondary, 0.7),
                        }}
                      >
                        {link.description}
                      </Typography>
                    </Box>
                    <ExternalLink
                      size={16}
                      aria-hidden="true"
                      style={{
                        color: alpha(theme.palette.text.secondary, 0.5),
                      }}
                    />
                  </ExternalLinkCard>
                </motion.div>
              ))}
            </Box>
          </MinimalCard>
        </motion.div>
      </Box>
    </PageContainer>
  );
};

export default HomePage;
